// cbrCalc.js — wrapper around labEngine cbrMould / cbrSpecimen / cbrAtCompaction (IS 2720 Part 16:1987, laboratory CBR).
import { cbrMould, cbrSpecimen, cbrAtCompaction, checkResolution } from '../labEngine';

const CLAUSE = 'IS 2720-16:1987';
export const PENS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 12.5]; // penetration, mm
export const REQUIRED_PENS = 9;                                        // 0 … 5.0 mm must be read

// IS 2720-16 §7.2 origin correction, suggested automatically: if the load–penetration curve is concave upward at the start,
// draw the tangent at the point of greatest slope and move the origin to where it cuts the penetration axis.
// Uses the steepest 0–5 mm segment as the tangent; returns 0 when the curve is steepest from the start (no correction needed).
// It is a suggestion — the technician can overwrite it with the value read from the plotted curve.
export function suggestOriginShift(readings, ringFactor, areaCm2) {
  const pts = readings.filter((r) => Number.isFinite(r.dial) && r.pen <= 5).map((r) => ({ pen: r.pen, y: (r.dial * ringFactor) / areaCm2 }));
  if (pts.length < 4 || !(ringFactor > 0) || !(areaCm2 > 0)) return 0;
  let best = -Infinity, k = 0;
  for (let i = 1; i < pts.length; i++) {
    const slope = (pts[i].y - pts[i - 1].y) / (pts[i].pen - pts[i - 1].pen);
    if (slope > best) { best = slope; k = i; }
  }
  if (k <= 1 || !(best > 0)) return 0;
  const x0 = pts[k - 1].pen - pts[k - 1].y / best;
  return x0 >= 0.1 ? Number(x0.toFixed(2)) : 0;
}

// moulds = [{ mouldNo, blows, mouldMassG, volumeCc, mouldSoilG, contNo, wetG, dryG, tareG, dsMm, drMm, heightMm, originShiftMm,
//             after: { mouldSoilG, contNo, wetG, dryG, tareG } | null, readings:[{pen, dial}] }]
export function computeCBR({ ringFactor, areaCm2, soakHours, compaction, replacedOversizeKg, labMddGcc = null, omcPct = null, surchargeKg = null, castingOn = '', testedOn = '', source = '', proposedUse = '', moulds }) {
  const issues = [];
  let bad = false;
  const block = (code, msg, clause = CLAUSE) => { bad = true; issues.push({ level: 'block', code, clause, msg }); };

  if (!(ringFactor > 0)) block('RING', 'Proving ring factor must be above 0');
  if (!(areaCm2 > 0)) block('AREA', 'Plunger area must be above 0');
  if (!moulds.length) block('MOULDS', 'Enter at least one mould');
  if (labMddGcc != null && !(labMddGcc > 0)) block('MDD', 'Lab MDD must be above 0');
  moulds.forEach((m, i) => {
    const tag = `Mould ${i + 1}`;
    if (!(m.blows > 0)) block('BLOWS', `${tag}: number of blows must be above 0`);
    if (!(m.volumeCc > 0) || !(m.mouldMassG > 0)) block('MOULD', `${tag}: mould weight and volume must be above 0`);
    else if (!(m.mouldSoilG > m.mouldMassG)) block('MOULD_SOIL', `${tag}: mould + soil must be more than the empty mould`);
    if (!(m.tareG > 0)) block('MC', `${tag}: container weight must be above 0`, 'IS 2720-2:1973');
    else if (!(m.dryG > m.tareG)) block('MC', `${tag}: container + dry must be more than the container`, 'IS 2720-2:1973');
    else if (!(m.wetG >= m.dryG)) block('MC', `${tag}: container + wet must be at least container + dry`, 'IS 2720-2:1973');
    if (!(m.heightMm > 0)) block('HEIGHT', `${tag}: specimen height must be above 0`);
    if (m.after) {
      const a = m.after;
      if (!(a.mouldSoilG > m.mouldMassG)) block('AFTER_SOAK', `${tag}: after soaking — mould + soil must be more than the empty mould`);
      if (!(a.tareG > 0)) block('AFTER_SOAK', `${tag}: after soaking — container weight must be above 0`, 'IS 2720-2:1973');
      else if (!(a.dryG > a.tareG)) block('AFTER_SOAK', `${tag}: after soaking — container + dry must be more than the container`, 'IS 2720-2:1973');
      else if (!(a.wetG >= a.dryG)) block('AFTER_SOAK', `${tag}: after soaking — container + wet must be at least container + dry`, 'IS 2720-2:1973');
    } else issues.push({ level: 'warn', code: 'AFTER_SOAK_MISSING', clause: CLAUSE, msg: `${tag}: after-soaking weights / moisture not entered — the report will show before-soaking values only` });
    const maxPen = Math.max(...m.readings.map((r) => r.pen));
    if (m.readings.length < REQUIRED_PENS || maxPen < 5 + m.originShiftMm)
      block('READINGS', `${tag}: dial readings must go at least to ${5 + m.originShiftMm} mm penetration (5 mm + origin shift)`);
    if (m.readings.some((r, j) => j && r.dial < m.readings[j - 1].dial))
      issues.push({ level: 'warn', code: 'DIAL_DROP', clause: CLAUSE, msg: `${tag}: dial reading drops as penetration increases — recheck` });
  });
  if (bad) return { result: null, issues };

  moulds.forEach((m, i) => {
    ['mouldMassG', 'mouldSoilG'].forEach((k) => issues.push(checkResolution(`Mould ${i + 1} ${k === 'mouldMassG' ? 'weight' : 'mould + soil'}`, m[k], 1, CLAUSE)));
    ['wetG', 'dryG', 'tareG'].forEach((k) => issues.push(checkResolution(`Mould ${i + 1} ${k}`, m[k], 0.01, 'IS 2720-2:1973')));
  });

  const rows = moulds.map((m, i) => {
    const bulk = (m.mouldSoilG - m.mouldMassG) / m.volumeCc;
    const w = ((m.wetG - m.dryG) / (m.dryG - m.tareG)) * 100;
    const dry = (100 * bulk) / (100 + w);
    const omcVar = Number.isFinite(omcPct) ? w - omcPct : null;
    if (omcVar != null && Math.abs(omcVar) > 1)
      issues.push({ level: 'warn', code: 'MOULD_MC', clause: CLAUSE, msg: `Mould ${i + 1}: moulding moisture ${w.toFixed(2)} % is ${omcVar > 0 ? 'above' : 'below'} the OMC (${omcPct}) by ${Math.abs(omcVar).toFixed(2)} % — check` });
    let after = null;
    if (m.after) {
      ['mouldSoilG'].forEach((k) => issues.push(checkResolution(`Mould ${i + 1} after soaking mould + soil`, m.after[k], 1, CLAUSE)));
      ['wetG', 'dryG', 'tareG'].forEach((k) => issues.push(checkResolution(`Mould ${i + 1} after soaking ${k}`, m.after[k], 0.01, 'IS 2720-2:1973')));
      const bulkA = (m.after.mouldSoilG - m.mouldMassG) / m.volumeCc;
      const wA = ((m.after.wetG - m.after.dryG) / (m.after.dryG - m.after.tareG)) * 100;
      after = { bulk: bulkA, w: wA, dry: (100 * bulkA) / (100 + wA) };
    }
    const c = cbrMould({ ringFactor, areaCm2, readings: m.readings, originShiftMm: m.originShiftMm });
    const s = cbrSpecimen({ soakHours, expansion: { dsMm: m.dsMm, drMm: m.drMm, heightMm: m.heightMm }, replacedOversizeKg, compaction });
    [...c.issues, ...s.issues].forEach((it) => issues.push({ ...it, msg: `Mould ${i + 1}: ${it.msg}` }));
    const curve = m.readings.map((r) => ({ pen: r.pen - m.originShiftMm, kgcm2: (r.dial * ringFactor) / areaCm2 }));
    return { bulk, w, dry, after, omcVar, ...c.result, expansionRatio: s.result.expansionRatio, curve, compactionPct: labMddGcc ? (dry / labMddGcc) * 100 : null };
  });
  // the engine reports each issue once per mould; keep the soak/oversize/compaction ones just once
  const seen = new Set();
  const uniq = issues.filter(Boolean).filter((it) => (['SOAK', 'OVERSIZE', 'COMPACTION'].includes(it.code) ? (seen.has(it.code) ? false : seen.add(it.code)) : true));

  // CBR at 95 / 97 / 100 % of lab MDD — needs ≥ 2 moulds at different densities
  let atMdd = null;
  const dryVals = rows.map((r) => r.dry);
  if (labMddGcc && rows.length >= 2) {
    if (new Set(dryVals.map((d) => d.toFixed(3))).size < 2)
      uniq.push({ level: 'warn', code: 'SAME_DENSITY', clause: CLAUSE, msg: 'Moulds have the same dry density — CBR at % of MDD cannot be interpolated' });
    else {
      const a = cbrAtCompaction({ points: rows.map((r) => ({ dry: r.dry, cbr: r.design })), mddGcc: labMddGcc });
      atMdd = a.result; uniq.push(...a.issues);
    }
  }
  const at97 = atMdd?.find((x) => x.level === 97);
  const result = {
    rows, atMdd, labMddGcc, omcPct, surchargeKg, castingOn, testedOn, source, proposedUse, compaction, soakHours, replacedOversizeKg,
    reportedCbr: at97 ? at97.cbr : rows[0].design,
    reportedLabel: at97 ? 'CBR at 97 % of MDD' : rows.length > 1 ? 'CBR (mould 1)' : 'CBR',
    maxExpansion: Math.max(...rows.map((r) => r.expansionRatio)),
  };
  return { result, issues: uniq };
}
